/* 
██╗██╗░░░██╗  ██████╗░██╗░██████╗░██╗████████╗░█████╗░██╗░░░░░
██║██║░░░██║  ██╔══██╗██║██╔════╝░██║╚══██╔══╝██╔══██╗██║░░░░░
██║██║░░░██║  ██║░░██║██║██║░░██╗░██║░░░██║░░░███████║██║░░░░░
██║██║░░░██║  ██║░░██║██║██║░░╚██╗██║░░░██║░░░██╔══██║██║░░░░░
██║╚██████╔╝  ██████╔╝██║╚██████╔╝██║░░░██║░░░██║░░██║███████╗
╚═╝░╚═════╝░  ╚═════╝░╚═╝░╚═════╝░╚═╝░░░╚═╝░░░╚═╝░░╚═╝╚══════╝
╔════════════════════════════════════════════════════════════╗
║              EQUIPO DE DESARROLLO DE SOFTWARE              ║
║                   MODULO DESARROLLADO POR                  ║
╟┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄┄╢
║ Victor Manuel Calle Londoño                                ║
║ victor.calle@est.iudigital.edu.co                          ║
╚════════════════════════════════════════════════════════════╝
*/
import React, { useState } from "react";
import {
  Card,
  Grid,
  Box,
  Chip,
  Divider,
  Table,
  TableContainer,
  TableBody,
  TableRow,
  TableCell,
  Paper,
  Pagination,
  Stack,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from "@mui/material";
import DataTable from "examples/Tables/DataTable";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDBadge from "components/MDBadge";
import PropTypes from "prop-types";
import GetAppIcon from "@mui/icons-material/GetApp";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import TableChartIcon from "@mui/icons-material/TableChart";
import SummarizeIcon from "@mui/icons-material/Summarize";
import { downloadFile } from "utils/fileUtils";
import { downloadReportExcel, downloadSumaryReportExcel } from "../services/reportsService";

// Constantes para tipos de carnet que SÍ requieren campos de tiempo
const CARD_TYPES_WITH_TIME_FIELDS = ["Invitado"];
import usePermission from "hooks/usePermission";

/**
 * Componente para mostrar la vista previa del reporte generado
 * Replica el diseño mostrado en la imagen de referencia
 *
 * @param {Object} props - Propiedades del componente
 * @param {Object} props.reportData - Datos del reporte generado
 * @param {Object} props.filters - Filtros aplicados al reporte
 * @param {Function} props.onDownloadExcel - Función para descargar Excel
 * @param {Function} props.onDownloadPDF - Función para descargar PDF
 * @param {Function} props.onBackToFilters - Función para regresar a filtros
 * @param {boolean} props.loading - Estado de carga
 * @param {number} props.entriesStart - Índice de inicio de entradas
 * @param {number} props.entriesEnd - Índice de fin de entradas
 * @param {number} props.totalData - Total de datos
 * @param {number} props.totalPages - Total de páginas
 * @param {number} props.currentPage - Página actual
 * @param {Function} props.handlePageChange - Función para cambiar de página
 * @param {Function} props.handleAlert - Función para mostrar alertas
 */
const ReportPreview = ({
  reportData,
  filters,
  onDownloadExcel,
  onDownloadPDF,
  onBackToFilters,
  loading,
  entriesStart,
  entriesEnd,
  totalData,
  totalPages,
  currentPage,
  handlePageChange,
  handleAlert,
}) => {
  const canDownloadReportAttendance = usePermission("Card.event.attendance.export");
  const canDownloadReportAttendanceSummary = usePermission("Card.event.attendance.export.summary");
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const handleMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleExcelDownload = async (type) => {
    const currentUrl = window.location.href;
    const dataSourceId = currentUrl.split("/Fuentes/")[1];

    try {
      const result =
        type === "detailed"
          ? await downloadReportExcel(dataSourceId)
          : await downloadSumaryReportExcel(dataSourceId);

      if (result.status !== false) {
        downloadFile(
          result.file,
          type === "detailed" ? "Reporte_Detallado.xlsx" : "Reporte_Resumen.xlsx",
          result.contentDisposition,
          () => console.log("Descarga exitosa"),
          (error) => console.error("Error en descarga:", error),
          "Archivo descargado con éxito",
          "Error al descargar el archivo"
        );
      } else {
        handleAlert("fail", result.message);
      }
    } catch (error) {
      handleAlert("fail", error.message);
      console.error("Error al descargar:", error);
    }

    handleMenuClose();
  };

  /**
   * Formatea una fecha para mostrar en el reporte
   * @param {string} date - Fecha en formato ISO
   * @returns {string} Fecha formateada con hora y minutos
   */
  const formatDate = (date) => {
    if (!date) return "";
    return new Date(date).toLocaleString("es-ES", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  /**
   * Formatea una hora para mostrar en el reporte
   * @param {string} time - Hora en formato HH:MM
   * @returns {string} Hora formateada
   */
  const formatTime = (time) => {
    if (!time) return "";
    return time;
  };

  // Verificar si el tipo de carnet requiere campos de tiempo
  const shouldShowTimeFields = CARD_TYPES_WITH_TIME_FIELDS.includes(reportData?.event?.card_type);

  return (
    <Card>
      <MDBox p={3}>
        {/* Encabezado del reporte */}
        <Box mb={4}>
          <Grid container spacing={2}>
            {/* Título principal */}
            <Grid item xs={12}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                mb={3}
                p={2}
                sx={{
                  backgroundColor: "#f8f9fa",
                  borderRadius: 2,
                  border: "1px solid #e9ecef",
                }}
              >
                <MDTypography variant="h5" color="dark" fontWeight="bold">
                  REPORTE DE ASISTENCIA
                </MDTypography>
                {(canDownloadReportAttendance || canDownloadReportAttendanceSummary) && (
                  <>
                    <MDButton
                      variant="contained"
                      color="success"
                      startIcon={<GetAppIcon />}
                      endIcon={<ArrowDropDownIcon />}
                      onClick={handleMenuClick}
                      disabled={loading}
                      size="small"
                    >
                      Descargar Excel
                    </MDButton>
                    <Menu
                      anchorEl={anchorEl}
                      open={open}
                      onClose={handleMenuClose}
                      MenuListProps={{
                        "aria-labelledby": "excel-download-button",
                      }}
                    >
                      <MenuItem onClick={() => handleExcelDownload("detailed")}>
                        <ListItemIcon>
                          <TableChartIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Reporte Detallado</ListItemText>
                      </MenuItem>
                      <MenuItem onClick={() => handleExcelDownload("summary")}>
                        <ListItemIcon>
                          <SummarizeIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText>Reporte Resumen</ListItemText>
                      </MenuItem>
                    </Menu>
                  </>
                )}
              </Box>
            </Grid>
          </Grid>

          {/* Tabla de información del evento */}
          <TableContainer component={Paper} sx={{ mb: 3 }}>
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}>
                    Nombre del evento:
                  </TableCell>
                  <TableCell colSpan={3}>{reportData?.event?.name || "No especificado"}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}>
                    Descripción:
                  </TableCell>
                  <TableCell colSpan={3}>
                    {reportData?.event?.description || "No especificado"}
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}>
                    Fecha de creación:
                  </TableCell>
                  <TableCell colSpan={shouldShowTimeFields ? 1 : 3}>
                    {formatDate(reportData?.event?.created_at) || "No especificada"}
                  </TableCell>
                  {shouldShowTimeFields && (
                    <>
                      <TableCell sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}>
                        Horas de ingreso anticipado
                      </TableCell>
                      <TableCell>
                        {formatTime(reportData?.event?.check_in_time) || "No especificada"}
                      </TableCell>
                    </>
                  )}
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}>
                    Tipo de carnet:
                  </TableCell>
                  <TableCell colSpan={shouldShowTimeFields ? 1 : 3}>
                    {reportData?.event?.card_type || "No especificado"}
                  </TableCell>
                  {shouldShowTimeFields && (
                    <>
                      <TableCell sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}>
                        Tiempo de permanencia después del evento
                      </TableCell>
                      <TableCell>
                        {formatTime(reportData?.event?.check_out_time) || "No especificada"}
                      </TableCell>
                    </>
                  )}
                </TableRow>
                <TableRow>
                  <TableCell sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}>
                    Total de carnets:
                  </TableCell>
                  <TableCell>{reportData?.event?.total_cards || 0}</TableCell>
                  {shouldShowTimeFields && (
                    <>
                      <TableCell sx={{ fontWeight: "bold", backgroundColor: "#f5f5f5" }}>
                        Asistieron:
                      </TableCell>
                      <TableCell>{reportData?.event?.attended_count || 0}</TableCell>
                    </>
                  )}
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Box>

        {/* Tabla de asistentes */}
        <DataTable
          table={{
            columns: [
              { Header: "No.", accessor: "number" },
              { Header: "Nombres y apellidos", accessor: "fullName" },
              { Header: "Número de documento", accessor: "numberId" },
              { Header: "Correo electrónico", accessor: "email" },
              { Header: "Tipo de carnet", accessor: "cardType" },
              ...(shouldShowTimeFields
                ? [
                    { Header: "Primera entrada", accessor: "firstEntry" },
                    { Header: "Última salida", accessor: "lastExit" },
                    { Header: "Asistencia", accessor: "attendance" },
                  ]
                : []),
              { Header: "Total lecturas", accessor: "totalReads" },
            ],
            rows:
              reportData?.data && Object.keys(reportData.data).length > 0
                ? Object.values(reportData.data).map((attendee, index) => ({
                    id: attendee.card_id || index,
                    number: index + 1,
                    fullName: attendee.name,
                    numberId: attendee.number_id,
                    email: attendee.email,
                    cardType: attendee.card_type,
                    totalReads: attendee.total_reads || 0,
                    ...(shouldShowTimeFields && {
                      firstEntry: attendee.first_entry
                        ? new Date(attendee.first_entry).toLocaleString("es-ES")
                        : "No registrada",
                      lastExit: attendee.last_exit
                        ? new Date(attendee.last_exit).toLocaleString("es-ES")
                        : "No registrada",
                      attendance: (
                        <MDBadge
                          badgeContent={attendee.attended ? "Asistió" : "No Asistió"}
                          color={attendee.attended ? "success" : "error"}
                          variant="gradient"
                          size="sm"
                        />
                      ),
                    }),
                  }))
                : [],
          }}
          canSearch={false}
          isSorted={true}
          entriesPerPage={false}
          showTotalEntries={false}
        />

        {/* Información de paginación y controles */}
        {reportData?.data && Object.keys(reportData.data).length > 0 ? (
          <>
            <MDBox style={{ marginLeft: "20px" }} mb={{ xs: 3, sm: 0 }}>
              <MDTypography
                style={{ left: "20px" }}
                variant="button"
                color="secondary"
                fontWeight="regular"
              >
                Mostrando resultados {entriesStart} - {entriesEnd} de un total de {totalData}
              </MDTypography>
            </MDBox>
            <Stack style={{ marginLeft: "20px", marginBottom: "10px" }} spacing={2}>
              <Pagination count={totalPages} page={currentPage} onChange={handlePageChange} />
            </Stack>
          </>
        ) : (
          <MDBox style={{ display: "flex", justifyContent: "center", alignItems: "center" }}>
            <MDTypography
              style={{ left: "20px", fontSize: "20px" }}
              variant="button"
              color="secondary"
              fontWeight="regular"
            >
              No hay datos de asistencia disponibles
            </MDTypography>
          </MDBox>
        )}
      </MDBox>
    </Card>
  );
};

ReportPreview.propTypes = {
  reportData: PropTypes.object,
  filters: PropTypes.object.isRequired,
  onDownloadExcel: PropTypes.func.isRequired,
  onDownloadPDF: PropTypes.func.isRequired,
  onBackToFilters: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  entriesStart: PropTypes.number,
  entriesEnd: PropTypes.number,
  totalData: PropTypes.number,
  totalPages: PropTypes.number,
  currentPage: PropTypes.number,
  handlePageChange: PropTypes.func,
  handleAlert: PropTypes.func,
};

export default ReportPreview;
